/**
 * Header component for chat channels showing channel info and actions.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  AvatarGroup,
  Menu,
  MenuItem,
  ListItemDecorator,
  Divider,
  Tooltip,
} from '@mui/joy';
import {
  Tag as TagIcon,
  Lock as LockIcon,
  Briefcase as WorkIcon,
  User as PersonIcon,
  Users as GroupIcon,
  UserPlus as PersonAddIcon,
  Info as InfoIcon,
  MagnifyingGlass as SearchIcon,
  DotsThreeVertical as MoreVertIcon,
  Bell as NotificationsIcon,
  BellSlash as NotificationsOffIcon,
  SignOut as LogoutIcon,
} from '@phosphor-icons/react';
import type { ChannelDetail, ChatUser } from '../../types/chat';

// Get first and last initials from a name (e.g., "Jeffrey Stilwell" -> "JS")
const getInitials = (name: string | undefined): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0][0]?.toUpperCase() || '?';
  }
  const first = parts[0][0]?.toUpperCase() || '';
  const last = parts[parts.length - 1][0]?.toUpperCase() || '';
  return first + last;
};

// Generate a consistent color based on a string (name/username)
const AVATAR_COLORS = [
  '#7C3AED', // violet
  '#2563EB', // blue
  '#0891B2', // cyan
  '#059669', // emerald
  '#D97706', // amber
  '#DC2626', // red
  '#DB2777', // pink
  '#7C3AED', // purple
];

const getAvatarColor = (name: string | undefined): string => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

interface ChatHeaderProps {
  channel: ChannelDetail | null;
  isLoading?: boolean;
  otherUser?: ChatUser | null; // For DM channels
  onShowInfo?: () => void;
  onShowMembers?: () => void;
  onInvite?: () => void;
  onSearch?: () => void;
  onLeave?: () => void;
  onMute?: (muted: boolean) => void;
  isMuted?: boolean;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  channel,
  isLoading = false,
  otherUser,
  onShowInfo,
  onShowMembers,
  onInvite,
  onSearch,
  onLeave,
  onMute,
  isMuted = false,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 150,
            height: 24,
            bgcolor: 'background.level2',
            borderRadius: 'sm',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      </Box>
    );
  }

  if (!channel) {
    return (
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography level="body-md" color="neutral">
          Select a channel to start chatting
        </Typography>
      </Box>
    );
  }

  const getChannelIcon = () => {
    switch (channel.channel_type) {
      case 'direct':
        return <PersonIcon />;
      case 'job':
        return <WorkIcon />;
      case 'group':
        return <GroupIcon />;
      default:
        return channel.is_private ? <LockIcon /> : <TagIcon />;
    }
  };

  const getChannelTitle = () => {
    if (channel.channel_type === 'direct' && otherUser) {
      return otherUser.full_name || otherUser.username;
    }
    return channel.name;
  };

  const getChannelSubtitle = () => {
    if (channel.channel_type === 'direct' && otherUser) {
      return otherUser.email;
    }
    if (channel.channel_type === 'job' && channel.job_name) {
      return `Job: ${channel.job_name}`;
    }
    if (channel.description) {
      return channel.description;
    }
    return `${channel.member_count} ${channel.member_count === 1 ? 'member' : 'members'}`;
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, md: 2 },
        bgcolor: 'background.surface',
      }}
    >
      {/* Channel Icon & Info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
        {/* Icon or Avatar */}
        {channel.channel_type === 'direct' && otherUser ? (
          <Avatar
            size="sm"
            src={otherUser.avatar_url || undefined}
            alt={otherUser.full_name || otherUser.username}
            sx={{
              bgcolor: otherUser.avatar_url ? undefined : getAvatarColor(otherUser.full_name || otherUser.username),
              color: 'white',
            }}
          >
            {getInitials(otherUser.full_name || otherUser.username)}
          </Avatar>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 'sm',
              bgcolor: 'background.level1',
              color: 'text.secondary',
            }}
          >
            {getChannelIcon()}
          </Box>
        )}

        {/* Channel Name & Description */}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            level="title-md"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {getChannelTitle()}
          </Typography>
          <Typography
            level="body-xs"
            color="neutral"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {getChannelSubtitle()}
          </Typography>
        </Box>
      </Box>

      {/* Member Avatars (for non-DM channels) */}
      {channel.channel_type !== 'direct' && channel.members && channel.members.length > 0 && (
        <Tooltip
          title={
            <Box sx={{ py: 0.5 }}>
              <Typography level="body-xs" fontWeight="lg" sx={{ mb: 0.5 }}>
                Members ({channel.members.length})
              </Typography>
              {channel.members.map((member) => (
                <Typography key={member.id} level="body-xs">
                  {member.user.full_name || member.user.username}
                </Typography>
              ))}
            </Box>
          }
          placement="bottom"
          variant="outlined"
        >
          <AvatarGroup
            sx={{
              '--AvatarGroup-gap': '-8px',
              '--Avatar-size': '28px',
              cursor: 'pointer',
              display: { xs: 'none', md: 'flex' },
            }}
            onClick={onShowMembers}
          >
            {channel.members.slice(0, 3).map((member) => (
              <Avatar
                key={member.id}
                size="sm"
                src={member.user.avatar_url || undefined}
                alt={member.user.full_name || member.user.username}
                sx={{
                  bgcolor: member.user.avatar_url ? undefined : getAvatarColor(member.user.full_name || member.user.username),
                  color: 'white',
                }}
              >
                {getInitials(member.user.full_name || member.user.username)}
              </Avatar>
            ))}
            {channel.members.length > 3 && (
              <Avatar size="sm">+{channel.members.length - 3}</Avatar>
            )}
          </AvatarGroup>
        </Tooltip>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {/* Search */}
        <Tooltip title="Search in channel">
          <IconButton
            variant="plain"
            color="neutral"
            size="sm"
            onClick={onSearch}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>

        {/* Mute/Unmute */}
        <Tooltip title={isMuted ? 'Unmute notifications' : 'Mute notifications'}>
          <IconButton
            variant="plain"
            color="neutral"
            size="sm"
            onClick={() => onMute?.(!isMuted)}
          >
            {isMuted ? <NotificationsOffIcon /> : <NotificationsIcon />}
          </IconButton>
        </Tooltip>

        {/* Info */}
        <Tooltip title="Channel info">
          <IconButton
            variant="plain"
            color="neutral"
            size="sm"
            onClick={onShowInfo}
          >
            <InfoIcon />
          </IconButton>
        </Tooltip>

        {/* More menu */}
        <IconButton
          variant="plain"
          color="neutral"
          size="sm"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>

      {/* More menu dropdown */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        placement="bottom-end"
      >
        {channel.channel_type !== 'direct' && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onInvite?.();
            }}
          >
            <ListItemDecorator>
              <PersonAddIcon />
            </ListItemDecorator>
            Invite people
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onShowMembers?.();
          }}
        >
          <ListItemDecorator>
            <GroupIcon />
          </ListItemDecorator>
          View members
        </MenuItem>
        <Divider />
        {channel.channel_type !== 'direct' && (
          <MenuItem
            color="danger"
            onClick={() => {
              setMenuAnchor(null);
              onLeave?.();
            }}
          >
            <ListItemDecorator>
              <LogoutIcon />
            </ListItemDecorator>
            Leave channel
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default ChatHeader;
